<template>
  <div class="h-full flex flex-col bg-dark-primary">

    <!-- Header -->
    <div class="flex-shrink-0 px-4 py-3 border-b border-dark flex items-center justify-between">
      <div>
        <h1 class="text-lg font-semibold text-dark-primary">Chat</h1>
        <p class="text-sm text-dark-secondary">Investigate this session with AI</p>
      </div>
      <button
        v-if="messages.length"
        @click="clearHistory"
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-dark-secondary hover:text-error hover:bg-error/10 rounded-lg transition"
        title="Clear chat history"
      >
        <Trash2 class="w-3.5 h-3.5" />
        Clear
      </button>
    </div>

    <!-- Messages -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">

      <!-- Empty state with suggested questions -->
      <template v-if="!messages.length && !loading">
        <div class="flex flex-col items-center justify-center py-8 text-center">
          <MessageSquare class="w-10 h-10 text-dark-secondary opacity-40 mb-3" />
          <p class="text-sm text-dark-secondary mb-1">Ask anything about this session</p>
          <p class="text-xs text-dark-tertiary mb-6">Powered by RAG — searches across detections, frames and compliance data</p>
        </div>

        <!-- Suggested questions -->
        <div class="space-y-2">
          <p class="text-xs text-dark-tertiary uppercase tracking-wide font-semibold mb-2">Suggested questions</p>
          <button
            v-for="q in suggestedQuestions" :key="q"
            @click.prevent="sendSuggestion(q); $event.target.blur()"
            class="w-full text-left px-3 py-2.5 bg-dark-tertiary border border-dark rounded-lg text-xs text-dark-primary hover:bg-dark-quaternary hover:border-accent/30 transition"
          >
            {{ q }}
          </button>
        </div>
      </template>

      <!-- Messages list -->
      <template v-else>
        <div v-for="(msg, idx) in messages" :key="idx" class="space-y-2">

          <!-- User message -->
          <div v-if="msg.role === 'user'" class="flex justify-end">
            <div class="max-w-[85%] px-3 py-2 bg-accent/20 rounded-lg">
              <p class="text-sm text-dark-primary">{{ msg.content }}</p>
            </div>
          </div>

          <!-- Assistant message -->
          <div v-else class="flex flex-col gap-2">
            <div v-if="msg.content" class="max-w-[95%] px-3 py-2 bg-dark-tertiary border border-dark rounded-lg">
              <p class="text-sm text-dark-primary leading-relaxed whitespace-pre-line">{{ cleanContent(msg.content) }}</p>
              <!-- Streaming cursor -->
              <span v-if="msg.streaming" class="inline-block w-1.5 h-3.5 bg-accent ml-0.5 animate-blink" />
            </div>

            <!-- Sources -->
            <div v-if="msg.sources?.length" class="flex flex-wrap gap-1.5 ml-1">
              <button
                v-for="source in sortedSources(msg.sources)" :key="source.frame_id"
                @click="$emit('seek-frame', source.frame_id)"
                class="flex items-center gap-1 px-2 py-1 bg-dark-secondary border border-dark rounded text-xs text-dark-secondary hover:text-accent hover:border-accent/40 transition font-mono"
                :title="source.snippet"
              >
                <Film class="w-3 h-3" />
                {{ formatFrameTime(source.frame_id) }}
              </button>
            </div>
          </div>

        </div>

        <!-- Thinking indicator -->
        <div v-if="thinking" class="flex items-center gap-2 px-3 py-2">
          <div class="flex gap-1">
            <div class="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style="animation-delay: 0ms" />
            <div class="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style="animation-delay: 150ms" />
            <div class="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" style="animation-delay: 300ms" />
          </div>
          <span class="text-xs text-dark-tertiary">Analyzing...</span>
        </div>
      </template>

    </div>

    <!-- Input -->
    <div class="flex-shrink-0 border-t border-dark p-3">
      <div class="flex items-end gap-2">
        <textarea
          ref="inputRef"
          v-model="inputText"
          @keydown.enter.exact.prevent="sendMessage"
          @keydown.enter.shift.exact="null"
          @input="autoResize"
          placeholder="Ask about this session..."
          rows="1"
          class="flex-1 bg-dark-tertiary border border-dark rounded-lg px-3 py-2 text-sm text-dark-primary placeholder-dark-tertiary resize-none transition chat-input"
          :disabled="thinking"
          style="max-height: 120px; outline: none !important; box-shadow: none !important; overflow: hidden;"
        />
        <button
          @click="sendMessage"
          :disabled="!inputText.trim() || thinking"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-accent rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-accent/90 transition"
        >
          <CornerDownLeft class="w-4 h-4" />
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { MessageSquare, CornerDownLeft, Film, Trash2 } from "lucide-vue-next";
import { useSessionStore } from "../stores/sessionStore";
import { useAuthStore } from "../stores/authStore";

const props = defineProps({
  session: { type: Object, default: null },
});

const emit = defineEmits(["seek-frame"]);

const sessionStore = useSessionStore();
const authStore    = useAuthStore();

// State
const messages         = ref([]);
const inputText        = ref("");
const thinking         = ref(false);
const loading          = ref(false);
const messagesContainer = ref(null);
const inputRef         = ref(null);

const currentSession = computed(() => props.session || sessionStore.currentSession);

const suggestedQuestions = computed(() => [
  "What happened in this session?",
  "What TTPs were detected?",
  "Which frameworks were violated?",
  "Was there any lateral movement or privilege escalation?",
  "Summarize the threat activity",
]);

// ---------------------------------------------------------------------------
// Load history on mount
// ---------------------------------------------------------------------------
onMounted(async () => {
  await loadHistory();
});

watch(() => currentSession.value?.id, async () => {
  messages.value = [];
  await loadHistory();
});

async function loadHistory() {
  const sessionId = currentSession.value?.id;
  if (!sessionId) return;

  loading.value = true;
  try {
    const response = await fetch(
      `${authStore.apiBase}/chat/history?session_id=${sessionId}`,
      { headers: { "X-API-Key": authStore.apiKey } }
    );

    if (response.ok) {
      const data = await response.json();
      messages.value = (data.messages ?? []).map(m => ({
        role: m.role,
        content: m.content,
        sources: [],
      }));
      await scrollToBottom();
    }
  } catch (err) {
    console.error("[ChatPanel] Failed to load history:", err);
  } finally {
    loading.value = false;
  }
}

// ---------------------------------------------------------------------------
// Send message via streaming
// ---------------------------------------------------------------------------
async function sendMessage() {
  const text = inputText.value.trim();
  if (!text || thinking.value) return;

  // Add user message
  messages.value.push({ role: "user", content: text });
  inputText.value = "";
  resetInputHeight();
  await scrollToBottom();

  // Add empty assistant message for streaming
  const assistantMsg = { role: "assistant", content: "", sources: [], streaming: true };
  messages.value.push(assistantMsg);
  thinking.value = true;

  try {
    const body = { question: text };
    const sessionId = currentSession.value?.id;
    if (sessionId) body.session_id = sessionId;

    const response = await fetch(`${authStore.apiBase}/chat/stream`, {
      method: "POST",
      headers: {
        "X-API-Key": authStore.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    // Read SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop(); // keep incomplete line

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === "token") {
            assistantMsg.content += event.content;
            await scrollToBottom();
          } else if (event.type === "done") {
            assistantMsg.sources = (event.sources ?? []);
            assistantMsg.streaming = false;
          }
        } catch {
          // ignore malformed lines
        }
      }
    }

  } catch (err) {
    console.error("[ChatPanel] Stream error:", err);
    assistantMsg.content = "Sorry, an error occurred. Please try again.";
    assistantMsg.streaming = false;
  } finally {
    thinking.value = false;
    await scrollToBottom();
  }
}

function sendSuggestion(question) {
  inputText.value = question;
  sendMessage();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function scrollToBottom() {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
}

function formatFrameTime(ms) {
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function cleanContent(text) {
  if (!text) return text;
  // Remove [id: 12345] references - they're redundant since sources appear as buttons below
  return text
    .replace(/\s*\[id:\s*\d+\]/g, "")
    .replace(/\s+\./g, ".")
    .replace(/\s+,/g, ",")
    .trim();
}

function sortedSources(sources) {
  // Filter out sources with fake frame_ids (timestamps as IDs) and sort by time
  return [...sources]
    .filter(s => s.frame_id < 1e12)
    .sort((a, b) => a.frame_id - b.frame_id);
}

function autoResize(e) {
  const el = e.target;
  el.style.height = "auto";
  el.style.height = Math.min(el.scrollHeight, 120) + "px";
}

function resetInputHeight() {
  if (inputRef.value) {
    inputRef.value.style.height = "auto";
  }
}

async function clearHistory() {
  messages.value = [];
}
</script>

<style scoped>
.segura-icon { color: #006b69; }
html[data-theme="dark"] .segura-icon,
.dark .segura-icon { color: #00d1b5; }

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 3px; }

.chat-input:focus,
.chat-input:focus-visible,
.chat-input:active {
  outline: none !important;
  box-shadow: none !important;
}

* {
  outline: none !important;
  box-shadow: none !important;
}

.animate-blink {
  animation: blink 1s step-end infinite;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.animate-bounce {
  animation: bounce 1s infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
</style>